import axios, { AxiosResponse } from 'axios';
import { Project, ProjectsResponse, HomeSummary, ProjectsFilters, ApiResponse, Risk, Task, Action, PortalUser, UserPermission, UserProfile, CostGroup, ProjectCost, ProjectCostSummary, Attachment, ExecutiveSummary, KpiTrends, ProjectPortfolio, Milestone, AlertConfig, AlertLog, AlertTestResult, IntakeRequest, IntakeRequestPayload, IntakeRequestListResponse, IntakeRequestUpdatePayload, GlobalSetting, CommitteeApprovalRequest, Committee, PortfolioFinancialSummaryResponse, ProjectFinancialDetailResponse } from '@/types';
import { AuditFeedResponse, AuditFeedFilters } from '@/types/auditFeed';

/**
 * sanitizeError — M-09 fix
 * Returns a safe user-facing message, hiding raw backend details in production.
 */
export function sanitizeError(error: unknown, fallback = 'Ocorreu um erro inesperado.'): string {
  if (!error) return fallback;
  const data = (error as any)?.response?.data;
  const detail = data?.detail ?? data?.message ?? (error as any)?.message ?? '';
  if (typeof detail === 'string' && detail.length > 0 && detail.length < 300) {
    return detail;
  }
  return fallback;
}

const ENV_BASE = (() => {
  try {
    const raw = (import.meta as any)?.env?.VITE_API_BASE_URL as string | undefined;
    if (!raw) return undefined;
    const trimmed = raw.replace(/\/+$/, '');
    return trimmed.length ? trimmed : undefined;
  } catch {
    return undefined;
  }
})();

const API_PREFIX = '/api/v1';
const BASE_URL = (() => {
  // Se VITE_API_BASE_URL está configurado, usar
  if (ENV_BASE) {
    const trimmed = ENV_BASE;
    if (trimmed.endsWith('/api/v1')) return trimmed;
    if (trimmed.endsWith('/api')) return `${trimmed}/v1`;
    return `${trimmed}${API_PREFIX}`;
  }

  // Usar URL relativa — Vite proxy (dev) ou Vercel rewrites (prod) fazem o roteamento
  return API_PREFIX;
})();

// API configuration loaded (log removed per Feature 001-remove-frontend-logs)
// Silent log helpers to satisfy lint (no-console)
const logDebug = (..._args: unknown[]) => { void _args; };
const logError = (..._args: unknown[]) => { void _args; };

const isDev = (() => {
  try {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return !!(import.meta && import.meta.env && import.meta.env.DEV);
  } catch {
    return false;
  }
})();



class ApiService {
  private client = axios.create({
    baseURL: BASE_URL,
    timeout: 60000,
    withCredentials: true,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(isDev ? { 'X-API-Key': 'DEV-ALLOW' } : {}),
    },
  });

  private authToken: string | null = null;

  /** Expose the configured axios instance so other services can reuse auth interceptors. */
  get axiosClient() {
    return this.client;
  }

  // Interceptor para tratamento de erros
  constructor() {
    this.client.defaults.withCredentials = true;
    // Load auth token from sessionStorage if available
    try {
      this.authToken = typeof window !== 'undefined' ? window.sessionStorage?.getItem('auth_token') : null;
    } catch {}
    
    // In dev, send a permissive header for backend dev-bypass on mutations only
    this.client.interceptors.request.use((config) => {
      try {
        // CRITICAL: Rewrite any HTTP URLs to HTTPS in production
        // This handles cases where relative URLs are being resolved to HTTP
        if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
          const url = String(config.url || '');
          if (url.startsWith('http://')) {
            config.url = url.replace(/^http:\/\//, 'https://');
          }
        }

        const method = (config.method || '').toLowerCase();
        const isMutation = method === 'post' || method === 'put' || method === 'patch' || method === 'delete';
        // vite exposes import.meta.env.DEV
        // header is ignored in prod; backend checks DEBUG too
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (isMutation && (import.meta?.env?.DEV || false)) {
          // Ensure header is set regardless of AxiosHeaders implementation
          if ((config.headers as any)?.set) {
            (config.headers as any).set('X-API-Key', 'DEV-ALLOW');
          } else {
            config.headers = { ...(config.headers as any), 'X-API-Key': 'DEV-ALLOW' } as any;
          }
          try {
            // Header validation logic (debug log removed per Feature 001-remove-frontend-logs)
          } catch {}
        }
        // CRITICAL: Always reload token from sessionStorage before each request
        // This ensures recently-set tokens (after login) are used immediately
        // without relying on stale this.authToken from constructor
        try {
          const storedToken = typeof window !== 'undefined' ? window.sessionStorage?.getItem('auth_token') : null;
          if (storedToken && storedToken !== this.authToken) {
            this.authToken = storedToken;
          }
        } catch {}
        
        // Inject Bearer token if available (fallback when cookies are blocked)
        if (this.authToken) {
          if ((config.headers as any)?.set) {
            (config.headers as any).set('Authorization', `Bearer ${this.authToken}`);
          } else {
            config.headers = { ...(config.headers as any), 'Authorization': `Bearer ${this.authToken}` } as any;
          }
        }

        // T036: Inject X-Tenant-ID header ONLY for super admin tenant-specific requests
        // For regular users, rely on tenant_id in JWT only (no header)
        // This prevents "Tenant ID mismatch" errors when header differs from JWT tenant_id
        try {
          if (typeof window !== 'undefined') {
            // Check if user is superadmin (stored in localStorage during login)
            const isSuperAdminStr = sessionStorage.getItem('is_superadmin');
            const isSuperAdmin = isSuperAdminStr === 'true';
            
            // ONLY send X-Tenant-ID header for superadmins
            // Regular users should NEVER send this header to avoid mismatch errors
            if (isSuperAdmin) {
              const selectedTenantId = localStorage.getItem('selectedTenantId');
              
              // Only send header if selectedTenantId is explicitly set (not null/empty)
              if (selectedTenantId && selectedTenantId !== 'null') {
                const tenantId = parseInt(selectedTenantId, 10);
                
                if (!isNaN(tenantId) && tenantId > 0) {
                  if ((config.headers as any)?.set) {
                    (config.headers as any).set('X-Tenant-ID', String(tenantId));
                  } else {
                    config.headers = { ...(config.headers as any), 'X-Tenant-ID': String(tenantId) } as any;
                  }
                }
              }
              // If selectedTenantId is null or not set, DON'T send header (Global View)
            }
            // Regular users: never send X-Tenant-ID header
          }
        } catch (tenantError) {
          // Silently fail if tenant ID injection fails (no-console)
          logDebug('Failed to inject X-Tenant-ID header:', tenantError);
        }
      } catch {}
      return config;
    });
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = (error && error.response && error.response.status) || 0;
        const url: string = (error && error.config && error.config.url) || '';
        const method = String(error?.config?.method || '').toLowerCase();

        // Silenciar 401 para reduzir ruido e redirecionar para login quando necessario
        if (status === 401) {
          console.error(`[API] 401 Unauthorized on ${url}`, {
            url,
            method,
            // M-10: do NOT log headers — they contain auth tokens
            hasAuthHeader: !!error?.config?.headers?.Authorization,
          });
          
          try {
            const isAuthEndpoint = /\/auth\//.test(url || '');
            const isLoginPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/login');
            if (!isAuthEndpoint && !isLoginPage && typeof window !== 'undefined') {
              const current = `${window.location.pathname}${window.location.search || ''}`;
              window.location.href = `/login?next=${encodeURIComponent(current)}`;
            }
          } catch {}
          // Nao loga 401
        } else {
          // Evitar poluir console com erros conhecidos (ex.: listagem de tarefas sem tabela/migrations)
          const isTasksGet = /\/tasks(\?|$|\/)/.test(url || '') && method === 'get';
          if (!isTasksGet) {
            logError('[api] http error', {
              url,
              status,
              message: (error && (error as any).message) || 'unknown',
              data: (error as any)?.response?.data,
            });
          }
        }
        const message = (error as any)?.response?.data?.error?.message || (error as any)?.response?.data?.detail || (error as any)?.response?.data?.message || (error as any)?.message || 'Unknown error';
        const data = (error as any)?.response?.data;
        return Promise.reject({
          status,
          message,
          data,
          url,
        });
      }
    );
  }

  private isUuid(value: string): boolean {
    // Simple UUID v4 pattern (accepts any valid UUID with hyphens)
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value));
  }

  private normalizeCommittee(raw: any): Committee {
    const rawMembers: any[] = Array.isArray(raw?.members) ? raw.members : [];
    // Map approval-committee members (user object + is_majority_holder) to legacy UI shape
    const members = rawMembers.map((m: any) => {
      if (m && (m.user || typeof m?.is_majority_holder === 'boolean')) {
        return {
          id: String(m?.id ?? ''),
          user_id: typeof m?.user?.id === 'number' ? m.user.id : (m?.user?.id ? Number(m.user.id) : undefined),
          user_name: m?.user?.name ?? undefined,
          area: m?.area ?? '',
          role_label: m?.role_label ?? '',
          is_sovereign: Boolean(m?.is_majority_holder),
        } as any;
      }
      // Assume it's already in legacy shape
      return m;
    });

    return {
      id: String(raw?.id ?? ''),
      name: raw?.name ?? '',
      description: raw?.description ?? undefined,
      is_active: Boolean(raw?.is_active ?? true),
      // Provide defaults if approval-committee payload doesn't include these
      quorum_min: Number(raw?.quorum_min ?? raw?.quorumMin ?? 2),
      votes_required: Number(raw?.votes_required ?? raw?.votesRequired ?? 1),
      scope_type: raw?.scope_type ?? raw?.scopeType ?? undefined,
      scope_ref_id: raw?.scope_ref_id ?? raw?.scopeRefId ?? undefined,
      created_by: raw?.created_by ? String(raw.created_by) : undefined,
      created_at: raw?.created_at ?? raw?.createdAt ?? '',
      updated_at: raw?.updated_at ?? raw?.updatedAt ?? '',
      members: members.length ? members : undefined,
      member_count: typeof raw?.member_count === 'number' ? raw?.member_count : (members.length ? members.length : undefined),
      sovereign_count: typeof raw?.sovereign_count === 'number' ? raw?.sovereign_count : undefined,
    };
  }

  private toNumber(value: unknown): number {
    if (value === null || value === undefined) return 0;
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : 0;
  }

  private normalizeCostGroup(raw: any): CostGroup {
    return {
      id: Number(raw?.id ?? 0),
      name: raw?.name ?? '',
      description: raw?.description ?? null,
      is_active: Boolean(raw?.is_active ?? true),
      created_at: raw?.created_at ?? '',
      updated_at: raw?.updated_at ?? '',
    };
  }

  private normalizeAttachment(raw: any): Attachment {
    return {
      id: Number(raw?.id ?? 0),
      project_id: raw?.project_id ?? null,
      entity_type: raw?.entity_type ?? '',
      entity_id: Number(raw?.entity_id ?? 0),
      file_name: raw?.file_name ?? '',
      url: raw?.url ?? '',
      mime_type: raw?.mime_type ?? null,
      uploaded_by: raw?.uploaded_by ?? null,
      uploaded_at: raw?.uploaded_at ?? null,
    };
  }

  private normalizeProjectCost(raw: any): ProjectCost {
    return {
      id: Number(raw?.id ?? 0),
      project_id: Number(raw?.project_id ?? raw?.project?.id ?? 0),
      group_id: raw?.group_id ?? raw?.group?.id ?? null,
      item_name: raw?.item_name ?? '',
      amount: this.toNumber(raw?.amount),
      is_approved: Boolean(raw?.is_approved),
      is_executed: Boolean(raw?.is_executed),
      approved_at: raw?.approved_at ?? null,
      executed_at: raw?.executed_at ?? null,
      created_at: raw?.created_at ?? '',
      updated_at: raw?.updated_at ?? '',
      group: raw?.group ? this.normalizeCostGroup(raw.group) : null,
      attachments: Array.isArray(raw?.attachments)
        ? raw.attachments.map((att: any) => this.normalizeAttachment(att))
        : [],
    };
  }

  private normalizeIntakeRequest(raw: any): IntakeRequest {
    const list = (value: any): string[] => (
      Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim()) : []
    );

    return {
      id: Number(raw?.id ?? 0),
      reference_code: raw?.reference_code ?? '',
      title: raw?.title ?? '',
      requester_name: raw?.requester_name ?? '',
      requester_email: raw?.requester_email ?? undefined,
      sponsor_name: raw?.sponsor_name ?? undefined,
      contact_channel: raw?.contact_channel ?? undefined,
      target_date: raw?.target_date ?? undefined,
      project_type: raw?.project_type ?? 'novo_contrato',
      complexity: raw?.complexity ?? 'baixa',
      involved_areas: list(raw?.involved_areas),
      impacted_areas: list(raw?.impacted_areas),
      expected_benefit: raw?.expected_benefit ?? undefined,
      notes: raw?.notes ?? undefined,
      budget_amount: raw?.budget_amount ?? null,  // New field
      status: raw?.status ?? 'Pendente',
      created_at: raw?.created_at ?? '',
      updated_at: raw?.updated_at ?? '',
      approved_by_user_id: raw?.approved_by_user_id ?? null,
      approval_date: raw?.approval_date ?? null,
      rejection_date: raw?.rejection_date ?? null,
      approval_reason: raw?.approval_reason ?? null,
      decision_reason: raw?.decision_reason ?? null,  // New field
      decision_by: raw?.decision_by ?? null,  // New field
      decision_at: raw?.decision_at ?? null,  // New field
      submitted_by: raw?.submitted_by
        ? {
            id: Number(raw?.submitted_by?.id ?? 0),
            name: raw?.submitted_by?.name ?? '',
            email: raw?.submitted_by?.email ?? undefined,
          }
        : null,
      approved_by: raw?.approved_by
        ? {
            id: Number(raw?.approved_by?.id ?? 0),
            name: raw?.approved_by?.name ?? '',
            email: raw?.approved_by?.email ?? undefined,
          }
        : null,
      decided_by: raw?.decided_by  // New field
        ? {
            id: Number(raw?.decided_by?.id ?? 0),
            name: raw?.decided_by?.name ?? '',
            email: raw?.decided_by?.email ?? undefined,
          }
        : null,
      // If the backend attaches the created project info when an intake is approved,
      // include the project id so the frontend can link to the project page.
      project_id: raw?.project_id ?? null,
    };
  }

  // Metodo generico para GET
  async get<T>(url: string, params?: Record<string, string>, cancelToken?: any): Promise<T> {
    try {
      const config: any = { params };
      if (cancelToken) {
        config.cancelToken = cancelToken;
      }
      const response: AxiosResponse<ApiResponse<T> | T> = await this.client.get(url, config);
      // Alguns endpoints retornam { data: T }, outros retornam T diretamente
      const raw: any = (response as any)?.data;
      const normalized = (raw && typeof raw === 'object' && 'data' in raw && (raw as any).data !== undefined)
        ? (raw as any).data
        : raw;
      return normalized as T;
    } catch (error: unknown) { const err = error as any; if (err?.message !== 'cancelled') { console.error('[api] GET error', { url, message: err?.message, status: err?.response?.status, data: err?.response?.data }); } throw err; }
  }

  // Metodo generico para POST
  async post<T>(url: string, data?: any): Promise<T> {
    try {
      const response: AxiosResponse<ApiResponse<T> | T> = await this.client.post(url, data, { withCredentials: true });
      const raw: any = (response as any)?.data;
      return ((raw && typeof raw === 'object' && 'data' in raw && (raw as any).data !== undefined)
        ? (raw as any).data
        : raw) as T;
    } catch (error: unknown) { const err = error as any; console.error('[api] POST error', { url, message: err?.message, status: err?.response?.status, data: err?.response?.data }); throw err; }
  }

  // Metodo generico para PUT
  async put<T>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<ApiResponse<T> | T> = await this.client.put(url, data, { withCredentials: true });
    const raw: any = (response as any)?.data;
    return ((raw && typeof raw === 'object' && 'data' in raw && (raw as any).data !== undefined)
      ? (raw as any).data
      : raw) as T;
  }

  // Metodo generico para PATCH
  async patch<T>(url: string, data?: any): Promise<T> {
    try {
      const response: AxiosResponse<ApiResponse<T> | T> = await this.client.patch(url, data, { withCredentials: true });
      const raw: any = (response as any)?.data;
      return ((raw && typeof raw === 'object' && 'data' in raw && (raw as any).data !== undefined)
        ? (raw as any).data
        : raw) as T;
    } catch (error: unknown) { const err = error as any; console.error('[api] PATCH error', { url, message: err?.message, status: err?.response?.status, data: err?.response?.data }); throw err; }
  }

  // Metodo generico para DELETE
  async delete<T>(url: string): Promise<T> {
    try {
      const response: AxiosResponse<ApiResponse<T> | T> = await this.client.delete(url, { withCredentials: true });
      const raw: any = (response as any)?.data;
      return ((raw && typeof raw === 'object' && 'data' in raw && (raw as any).data !== undefined)
        ? (raw as any).data
        : raw) as T;
    } catch (error: unknown) { const err = error as any; console.error('[api] DELETE error', { url, message: err?.message, status: err?.response?.status, data: err?.response?.data }); throw err; }
  }

  async submitIntakeRequest(payload: IntakeRequestPayload): Promise<IntakeRequest> {
    const raw = await this.post<IntakeRequest>('/intake/requests', payload);
    return this.normalizeIntakeRequest(raw);
  }

  async createIntakeRequest(payload: IntakeRequestPayload): Promise<IntakeRequest> {
    const raw = await this.post<IntakeRequest>('/intake/requests', payload);
    return this.normalizeIntakeRequest(raw);
  }

  async listIntakeRequests(filters: {
    page?: number;
    page_size?: number;
    search?: string;
    status?: string;
    project_type?: string;
    complexity?: string;
    date_start?: string;
    date_end?: string;
    tenant_id?: number | null;
  } = {}): Promise<IntakeRequestListResponse> {
    const params: Record<string, string> = {};

    if (filters.page) params.page = String(filters.page);
    if (filters.page_size) params.page_size = String(filters.page_size);
    if (filters.search) params.search = filters.search;
    if (filters.status) params.status = filters.status;
    if (filters.project_type) params.project_type = filters.project_type;
    if (filters.complexity) params.complexity = filters.complexity;
    if (filters.date_start) params.date_start = filters.date_start;
    if (filters.date_end) params.date_end = filters.date_end;
    if (filters.tenant_id !== undefined && filters.tenant_id !== null) params.tenant_id = String(filters.tenant_id);

    const raw = await this.get<any>('/intake/requests', params);
    const items = Array.isArray(raw?.items) ? raw.items.map((item: any) => this.normalizeIntakeRequest(item)) : [];

    return {
      items,
      total: this.toNumber(raw?.total),
      page: this.toNumber(raw?.page) || filters.page || 1,
      page_size: this.toNumber(raw?.page_size) || filters.page_size || items.length || 0,
    };
  }

  async getIntakeRequest(id: number): Promise<IntakeRequest> {
    const raw = await this.get<any>(`/intake/requests/${id}`);
    return this.normalizeIntakeRequest(raw);
  }

  async updateIntakeRequest(id: number, payload: IntakeRequestUpdatePayload): Promise<IntakeRequest> {
    const raw = await this.put<any>(`/intake/requests/${id}`, payload);
    return this.normalizeIntakeRequest(raw);
  }

  async deleteIntakeRequest(id: number): Promise<void> {
    await this.delete<void>(`/intake/requests/${id}`);
  }

  async approveIntakeRequest(id: number, reason: string, notes?: string, managerId?: number): Promise<IntakeRequest> {
    const raw = await this.post<any>(`/intake/requests/${id}/approve`, {
      decision_reason: reason,
      notes,
      ...(managerId !== undefined && { manager_id: managerId }),
    });
    return this.normalizeIntakeRequest(raw);
  }

  async rejectIntakeRequest(id: number, reason: string, notes?: string): Promise<IntakeRequest> {
    const raw = await this.post<any>(`/intake/requests/${id}/reject`, { decision_reason: reason, notes });
    return this.normalizeIntakeRequest(raw);
  }

  // Unified approval pattern (new - T047)
  async approveIntakeRequestUnified(
    id: number,
    data: { is_approved: boolean; rejection_reason?: string; notes?: string }
  ): Promise<IntakeRequest> {
    const raw = await this.post<any>(`/intake/requests/${id}/approve`, data);
    return this.normalizeIntakeRequest(raw);
  }

  // Endpoints especificos

  // Home/Summary
  async getHomeSummary(): Promise<HomeSummary> {
    const raw: any = await this.get<any>('/home/summary');

    // Se ja estiver no formato esperado, retorne diretamente
    if (raw && typeof raw === 'object' && 'kpis' in raw) {
      return raw as HomeSummary;
    }

    // Mapeamento defensivo para diferentes formatos de backend
    const kpis = {
      active_projects: Number(
        raw?.kpis?.active_projects ??
        raw?.active_projects ??
        raw?.total_active ??
        raw?.total_projects ??
        0
      ),
      green_percent: Number(
        raw?.kpis?.green_percent ??
        raw?.green_percent ??
        raw?.status_percentages?.green ??
        0
      ),
      at_risk_projects: Number(
        raw?.kpis?.at_risk_projects ??
        raw?.at_risk_projects ??
        raw?.risk_count ??
        (Array.isArray(raw?.risk_projects) ? raw.risk_projects.length : 0)
      ),
      no_update_week: Number(
        raw?.kpis?.no_update_week ??
        raw?.no_update_week ??
        raw?.no_update ??
        0
      ),
      trends: {
        active_projects: Number(
          raw?.kpis?.trends?.active_projects ??
          raw?.active_projects_trend ?? 0
        ),
        green_percent: Number(
          raw?.kpis?.trends?.green_percent ??
          raw?.green_percent_trend ?? 0
        ),
        at_risk_projects: Number(
          raw?.kpis?.trends?.at_risk_projects ??
          raw?.at_risk_projects_trend ?? 0
        ),
        no_update_week: Number(
          raw?.kpis?.trends?.no_update_week ??
          raw?.no_update_week_trend ?? 0
        ),
      }
    } as any;

    // Lista de riscos pode vir como risk_projects, risks ou similar
    const rawRisks: any[] = Array.isArray(raw?.risk_projects)
      ? raw.risk_projects
      : (Array.isArray(raw?.risks?.items) ? raw.risks.items : (Array.isArray(raw?.risks) ? raw.risks : []));

    const risk_projects = rawRisks.map((r: any) => ({
      project_id: String(r.project_id ?? r.id ?? ''),
      project_code: r.project_code ?? r.code ?? r.project?.code ?? undefined,
      project_title: r.project_title ?? r.title ?? r.project?.title ?? '-',
      label: (r.label === 'critical' || r.label === 'attention') ? r.label : 'attention',
      next_step: r.next_step ?? r.nextStep ?? undefined,
      responsible_name: r.responsible_name ?? r.owner_name ?? r.owner?.name ?? undefined,
    }));

    // Atualizacoes recentes
    const rawUpdates: any[] = Array.isArray(raw?.updates)
      ? raw.updates
      : (Array.isArray(raw?.recent_updates) ? raw.recent_updates : (Array.isArray(raw?.latest_updates) ? raw.latest_updates : []));

    const updates = rawUpdates.map((u: any) => ({
      project_id: String(u.project_id ?? u.project?.id ?? ''),
      project_code: u.project_code ?? u.project?.code ?? undefined,
      project_title: u.project_title ?? u.project?.title ?? undefined,
      title: u.title ?? u.name ?? '-',
      created_at: u.created_at ?? u.createdAt ?? u.date ?? new Date().toISOString(),
    }));

    return { kpis, risk_projects, updates } as HomeSummary;
  }

  // Get KPIs with period-based filtering and comparison
  async getKPIsByPeriod(period: '7d' | '30d' | 'quarter' = '7d'): Promise<any> {
    return this.get<any>('/dashboard/kpis', { period });
  }

  // Projects
  async getProjects(filters?: ProjectsFilters): Promise<ProjectsResponse> {
    const params: Record<string, string> = {};

    // Map legacy `q` to backend `search`
    if (filters?.search) params.search = filters.search;
    else if (filters?.q) params.search = filters.q;

    // Priority
    if (filters?.priority) params.priority = filters.priority;

    // Status color filter
    if (filters?.status_color) params.status_color = filters.status_color;

    // Roadmap filters
    if (filters?.start_date_from) params.start_date_from = filters.start_date_from;
    if (filters?.start_date_to) params.start_date_to = filters.start_date_to;
    if (typeof filters?.roadmap === 'boolean') params.roadmap = String(filters.roadmap);

    // Pagination
    if (typeof filters?.limit === 'number') params.limit = String(filters.limit);
    if (typeof filters?.offset === 'number') params.offset = String(filters.offset);

    // Tenant scoping
    if (typeof filters?.tenant_id === 'number') params.tenant_id = String(filters.tenant_id);

    // Lifecycle status filter (default: active)
    if (filters?.lifecycle_status) params.lifecycle_status = filters.lifecycle_status;

    if (typeof filters?.has_active_risks === 'boolean') {
      params.has_active_risks = String(filters.has_active_risks);
    }

    // Keep enriched flag enabled by default
    params.enriched = 'true';

    return this.get<ProjectsResponse>('/projects', params);
  }

  async getProject(id: string): Promise<Project> {
    return this.get<Project>(`/projects/${encodeURIComponent(id)}`);
  }

  async getProjectStats(id: string) {
    return this.get<import('@/types').ProjectStats>(`/projects/${encodeURIComponent(id)}/stats`);
  }

  async createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project> {
    return this.post<Project>('/projects', project);
  }

  async updateProject(id: string, project: Partial<Project>): Promise<Project> {
    return this.put<Project>(`/projects/${encodeURIComponent(id)}`, project);
  }

  async deleteProject(id: string): Promise<void> {
    return this.delete<void>(`/projects/${encodeURIComponent(id)}`);
  }

  async sendProjectReport(projectId: string, additionalEmails?: string[]): Promise<{ status: string }> {
    return this.post<{ status: string }>(
      `/projects/${encodeURIComponent(projectId)}/report/send`,
      additionalEmails ? { additional_emails: additionalEmails } : {},
    );
  }

  // Project Members (Feature 001)
  async getProjectMembers(
    projectId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<import('@/types').ProjectMembersResponse> {
    const params: Record<string, string> = {
      page: String(page),
      page_size: String(pageSize),
    };
    return this.get<import('@/types').ProjectMembersResponse>(
      `/projects/${encodeURIComponent(projectId)}/members`,
      params
    );
  }

  async addProjectMember(
    projectId: string,
    payload: import('@/types').ProjectMemberCreatePayload
  ): Promise<import('@/types').ProjectMember> {
    return this.post<import('@/types').ProjectMember>(
      `/projects/${encodeURIComponent(projectId)}/members`,
      payload
    );
  }

  async updateProjectMember(
    projectId: string,
    userId: number,
    payload: import('@/types').ProjectMemberUpdatePayload
  ): Promise<import('@/types').ProjectMember> {
    return this.put<import('@/types').ProjectMember>(
      `/projects/${encodeURIComponent(projectId)}/members/${userId}`,
      payload
    );
  }

  async removeProjectMember(
    projectId: string,
    userId: number
  ): Promise<void> {
    return this.delete<void>(
      `/projects/${encodeURIComponent(projectId)}/members/${userId}`
    );
  }

  // FAQ
  async getFaq(): Promise<any[]> {
    return this.get<any[]>('/faq');
  }

  // Highlights
  async getHighlights(): Promise<any[]> {
    return this.get<any[]>('/highlights');
  }

  // Reports
  async getReports(): Promise<any[]> {
    return this.get<any[]>('/reports');
  }

  // Risks
  async getRisks(projectId?: string, filters?: { status?: string; label?: string; q?: string; limit?: number; offset?: number; tenant_id?: number }, cancelToken?: any): Promise<Risk[]> {
    const params: Record<string, string> = {};

    if (projectId) params.project_id = projectId;
    if (filters?.status) params.status = filters.status;
    if (filters?.label) params.label = filters.label;
    if (filters?.q) params.q = filters.q;
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.offset) params.offset = String(filters.offset);
    if (typeof filters?.tenant_id === 'number') params.tenant_id = String(filters.tenant_id);

    return this.get<Risk[]>('/risks', params, cancelToken);
  }

  async getRisk(id: string): Promise<Risk> {
    return this.get<Risk>(`/risks/${encodeURIComponent(id)}`);
  }

  async createRisk(risk: Omit<Risk, 'id' | 'created_at' | 'updated_at'>): Promise<Risk> {
    return this.post<Risk>('/risks', risk);
  }

  async updateRisk(id: string, risk: Partial<Risk>): Promise<Risk> {
    return this.put<Risk>(`/risks/${encodeURIComponent(id)}`, risk);
  }

  async deleteRisk(id: string): Promise<void> {
    return this.delete<void>(`/risks/${encodeURIComponent(id)}`);
  }

  // Tasks (pmo_task)
  async getTasks(projectId?: string, filters?: { status?: string; milestoneId?: string; q?: string; limit?: number; offset?: number }, cancelToken?: any): Promise<Task[]> {
    const params: Record<string, string> = {};
    if (projectId) params.project_id = projectId;
    if (filters?.status) params.status = filters.status;
    if (filters?.milestoneId) params.milestone_id = filters.milestoneId;
    if (filters?.q) params.search = filters.q;
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.offset !== undefined) params.skip = String(filters.offset);
    try {
      return await this.get<Task[]>('/tasks', params, cancelToken);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404 && projectId) {
        try {
          return await this.get<Task[]>(`/projects/${encodeURIComponent(projectId)}/tasks`, {}, cancelToken);
        } catch {}
      }
      throw err;
    }
  }

  async getTask(id: string): Promise<Task> {
    return this.get<Task>(`/tasks/${encodeURIComponent(id)}`);
  }

  async createTask(task: Omit<Task, 'id' | 'created_at' | 'updated_at'>): Promise<Task> {
    return this.post<Task>('/tasks', task);
  }

  async updateTask(id: string, task: Partial<Task>): Promise<Task> {
    return this.put<Task>(`/tasks/${encodeURIComponent(id)}`, task);
  }

  async deleteTask(id: string): Promise<void> {
    return this.delete<void>(`/tasks/${encodeURIComponent(id)}`);
  }

  // Actions (pmo_action)
  async getActions(
    projectId?: string,
    filters?: {
      owner_id?: string;
      status?: string;
      priority?: string;
      q?: string;
      limit?: number;
      offset?: number;
      overdue_only?: boolean;
    },
    cancelToken?: any
  ): Promise<Action[]> {
    const params: Record<string, string> = {};
    if (projectId) params.project_id = projectId;
    if (filters?.owner_id) params.owner_id = String(filters.owner_id);
    if (filters?.status) params.status = filters.status;
    if (filters?.priority) params.priority = filters.priority;
    if (filters?.q) params.search = filters.q;
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.offset) params.offset = String(filters.offset);
    if (typeof filters?.overdue_only === 'boolean' && filters.overdue_only) params.overdue_only = 'true';
    return this.get<Action[]>('/actions', params, cancelToken);
  }

  async getAction(id: string): Promise<Action> {
    return this.get<Action>(`/actions/${encodeURIComponent(id)}`);
  }

  async createAction(action: Omit<Action, 'id' | 'created_at' | 'updated_at' | 'deleted_at'>): Promise<Action> {
    return this.post<Action>('/actions', action);
  }

  async updateAction(id: string, action: Partial<Action>): Promise<Action> {
    return this.put<Action>(`/actions/${encodeURIComponent(id)}`, action);
  }

  async deleteAction(id: string): Promise<void> {
    return this.delete<void>(`/actions/${encodeURIComponent(id)}`);
  }

  // Costs & Budget
  async getCostGroups(): Promise<CostGroup[]> {
    const raw = await this.get<any[]>('/cost-groups');
    return (raw || []).map((item) => this.normalizeCostGroup(item));
  }

  async createCostGroup(data: { name: string; description?: string | null }): Promise<CostGroup> {
    const payload = {
      name: data.name,
      description: data.description ?? null,
    };
    const raw = await this.post<CostGroup>('/cost-groups', payload);
    return this.normalizeCostGroup(raw);
  }

  async updateCostGroup(id: number, data: { name?: string; description?: string | null; is_active?: boolean }): Promise<CostGroup> {
    const raw = await this.put<CostGroup>(`/cost-groups/${id}`, data);
    return this.normalizeCostGroup(raw);
  }

  async deleteCostGroup(id: number): Promise<void> {
    await this.delete<void>(`/cost-groups/${id}`);
  }

  async getProjectCosts(projectId: number): Promise<ProjectCost[]> {
    const raw = await this.get<any[]>(`/projects/${projectId}/costs`);
    return (raw || []).map((item) => this.normalizeProjectCost(item));
  }

  async createProjectCost(projectId: number, payload: Omit<ProjectCost, 'id' | 'project_id' | 'created_at' | 'updated_at' | 'group' | 'attachment'>): Promise<ProjectCost> {
    const raw = await this.post<ProjectCost>(`/projects/${projectId}/costs`, {
      group_id: payload.group_id,
      item_name: payload.item_name,
      amount: payload.amount,
      is_approved: payload.is_approved,
      is_executed: payload.is_executed,
      approved_at: payload.approved_at,
      executed_at: payload.executed_at,
    });
    return this.normalizeProjectCost(raw);
  }

  async updateProjectCost(costId: number, payload: Partial<Omit<ProjectCost, 'id' | 'project_id' | 'created_at' | 'updated_at' | 'group' | 'attachment'>>): Promise<ProjectCost> {
    const raw = await this.put<ProjectCost>(`/projects/costs/${costId}`, payload);
    return this.normalizeProjectCost(raw);
  }

  async deleteProjectCost(costId: number): Promise<void> {
    await this.delete<void>(`/projects/costs/${costId}`);
  }

  async getProjectCostSummary(projectId: number): Promise<ProjectCostSummary> {
    const raw = await this.get<any>(`/projects/${projectId}/costs/summary`);
    return {
      project_id: Number(raw?.project_id ?? projectId),
      budget: this.toNumber(raw?.budget),
      total_cost: this.toNumber(raw?.total_cost),
      approved_total: this.toNumber(raw?.approved_total),
      executed_total: this.toNumber(raw?.executed_total),
      remaining_budget: this.toNumber(raw?.remaining_budget),
      variance: this.toNumber(raw?.variance),
    };
  }

  async listAttachments(entityType: string, entityId: number): Promise<Attachment[]> {
    const raw = await this.get<any[]>(`/attachments/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`);
    return (raw || []).map((item) => this.normalizeAttachment(item));
  }

  async uploadAttachment(params: { entityType: string; entityId: number; file: File; projectId?: number; uploadedBy?: number; onProgress?: (percent: number) => void }): Promise<Attachment> {
    const formData = new FormData();
    formData.append('entity_type', params.entityType);
    formData.append('entity_id', String(params.entityId));
    formData.append('file', params.file);
    if (params.projectId !== undefined) {
      formData.append('project_id', String(params.projectId));
    }
    if (params.uploadedBy !== undefined) {
      formData.append('uploaded_by', String(params.uploadedBy));
    }

    const response = await this.client.post('/attachments/upload', formData, {
      timeout: Math.max(this.client.defaults.timeout ?? 0, 120000),
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (!event.total) return;
        const percent = Math.round((event.loaded / event.total) * 100);
        params.onProgress?.(percent);
      },
    });
    return this.normalizeAttachment(response.data);
  }

  async downloadAttachment(attachmentId: number): Promise<void> {
    try {
      const response = await this.client.get(`/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      
      // Extract filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'file';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=(["']?)([^"';]*)\1/);
        filename = match?.[2] || 'file';
      }
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  }

  async previewAttachment(attachmentId: number): Promise<void> {
    // Open the preview endpoint URL directly in a new tab.
    // The browser sends cookies automatically (same-origin via Vite proxy / Vercel rewrite),
    // and the server returns Content-Disposition: inline + correct Content-Type, so the
    // browser renders PDFs and images natively without a blob URL intermediary.
    // Using blob URLs caused Chrome 130+ to display raw PDF bytes instead of the viewer.
    const previewUrl = `${BASE_URL}/attachments/${attachmentId}/preview`;
    const newWindow = window.open(previewUrl, '_blank');
    if (!newWindow) {
      // Popup blocked — fall back to forced download so the user is never stuck
      await this.downloadAttachment(attachmentId);
    }
  }

  async deleteAttachment(attachmentId: number): Promise<void> {
    await this.delete<void>(`/attachments/${attachmentId}`);
  }

  // Users
  async listUsers(filters?: {
    search?: string;
    permission?: UserPermission;
    active_only?: boolean;
    approved_only?: boolean;
    limit?: number;
    offset?: number;
    tenant_id?: number;
  }): Promise<PortalUser[]> {
    const params: Record<string, string> = {};

    if (filters?.search) params.search = filters.search;
    if (filters?.permission) params.permission = filters.permission;
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.offset) params.offset = String(filters.offset);
    if (filters?.active_only === false) params.active_only = 'false';
    if (typeof filters?.approved_only === 'boolean') params.approved_only = String(filters.approved_only);
    if (typeof filters?.tenant_id === 'number') params.tenant_id = String(filters.tenant_id);

    return this.get<PortalUser[]>('/users', params);
  }

  async getUsers(): Promise<PortalUser[]> {
    // Return strong-typed users to avoid losing numeric IDs (used by MembersTab filtering)
    return this.listUsers({ active_only: true, approved_only: true, limit: 500 });
  }

  async createUser(payload: {
    name: string;
    email?: string;
    role_title?: string | null;
    permission: UserPermission;
    active?: boolean;
    is_approved?: boolean;
    password?: string;
  }): Promise<PortalUser> {
    return this.post<PortalUser>('/users', payload);
  }

  async updateUser(
    userId: number,
    payload: Partial<{
      name: string;
      email?: string;
      role_title?: string | null;
      permission: UserPermission;
      active: boolean;
      is_approved: boolean;
      password: string;
    }>,
  ): Promise<PortalUser> {
    return this.put<PortalUser>(`/users/${encodeURIComponent(String(userId))}`, payload);
  }

  async deleteUser(userId: number): Promise<void> {
    await this.delete<void>(`/users/${encodeURIComponent(String(userId))}`);
  }

  // Auth
  async login(email: string, password: string): Promise<{ message: string; user: PortalUser; token?: string }> {
    const response = await this.post<{ message: string; user: PortalUser; token?: string }>('/auth/login', { email, password });
    // Store token in sessionStorage for Bearer fallback if cookies fail
    if (response.token) {
      try {
        this.authToken = response.token;
        if (typeof window !== 'undefined') {
          window.sessionStorage?.setItem('auth_token', response.token);
        }
      } catch (e) {
        // Unable to store token in sessionStorage (silenciado)
      }
    }
    return response;
  }

  setAuthToken(token: string): void {
    this.authToken = token;
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.setItem('auth_token', token);
      }
    } catch (e) {
      // Unable to store token in sessionStorage (silenciado)
    }
  }

  clearAuthToken(): void {
    this.authToken = null;
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.removeItem('auth_token');
      }
    } catch (e) {
      // Unable to clear token from sessionStorage (silenciado)
    }
  }

  async logout(): Promise<void> {
    this.clearAuthToken();
    await this.post('/auth/logout');
  }

  async me(): Promise<PortalUser> {
    return this.get('/auth/me');
  }

  async getMyProfile(): Promise<UserProfile> {
    return this.get('/users/me/profile');
  }

  async updateMyProfile(payload: Partial<{
    email: string;
    name: string;
    role_title: string;
    pronouns: string;
    department: string;
    location: string;
    phone_number: string;
    mobile_number: string;
    linkedin_url: string;
    teams_url: string;
    timezone: string;
    bio: string;
    skills: string[];
    email_alerts_enabled: boolean;
  }>): Promise<UserProfile> {
    return this.put('/users/me/profile', payload);
  }

  async uploadProfilePhoto(file: File): Promise<{ profile_photo_url: string; profile_photo_updated_at?: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await this.client.post('/users/me/profile/photo', formData, {
      withCredentials: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const raw: any = response?.data;
    return (raw && raw.data) ? raw.data : raw;
  }

  async getProfilePhotoBlob(): Promise<Blob> {
    const response = await this.client.get('/users/me/profile/photo', {
      responseType: 'blob',
      withCredentials: true,
    });
    return response.data as Blob;
  }

  async changeMyPassword(payload: { current_password: string; new_password: string }): Promise<{ message: string }> {
    return this.post('/users/me/profile/password', payload);
  }

  async register(name: string, email: string, password: string): Promise<any> {
    return this.post('/auth/register', { name, email, password });
  }

  async forgotPassword(email: string): Promise<any> {
    return this.post('/auth/forgot-password', { email });
  }

  async resetPassword(token: string, password: string): Promise<any> {
    return this.post('/auth/reset-password', { token, password });
  }

  async listPendingUsers(): Promise<Array<{
    id: number;
    name: string;
    email?: string | null;
    role_title?: string | null;
    permission: UserPermission;
    created_at?: string | null;
  }>> {
    return this.get('/auth/pending-users');
  }

  async approveUser(userId: number, payload: { permission?: UserPermission; role_title?: string | null }): Promise<{ message: string; user: PortalUser }> {
    return this.post(`/auth/approve-user/${userId}`, payload);
  }

  // Programs
  async getPrograms(): Promise<Array<{ id: number; name: string }>> {
    const response = await this.get<{ items: Array<{ id: number; name: string }>; total: number; page: number; page_size: number }>('/programs');
    return response.items || [];
  }

  /**
   * Get executive dashboard summary with KPIs, status distribution, and trends
   */
  async getExecutiveSummary(): Promise<ExecutiveSummary> {
    try {
      return await this.get<ExecutiveSummary>('/executive/summary');
    } catch (error) {
      console.error('[api] Unable to load reports', error);
      throw error;
    }
  }

  /**
   * Get KPI trends showing historical data for last 12 months
   */
  async getKpiTrends(): Promise<KpiTrends> {
    try {
      return await this.get<KpiTrends>('/executive/kpi-trends');
    } catch (error) {
      console.error('[api] Unable to load reports', error);
      throw error;
    }
  }

  // Milestones
  async getMilestones(projectId?: number, filters?: { q?: string; limit?: number; offset?: number }): Promise<Milestone[]> {
    const params: Record<string, string> = {};
    if (projectId) params.project_id = String(projectId);
    if (filters?.q) params.search = filters.q;
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.offset) params.offset = String(filters.offset);
    return this.get<Milestone[]>('/milestones', params);
  }

  async getMilestone(id: number): Promise<Milestone> {
    return this.get<Milestone>(`/milestones/${id}`);
  }

  async createMilestone(milestone: Omit<Milestone, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by' | 'deleted_at'>): Promise<Milestone> {
    return this.post<Milestone>('/milestones', milestone);
  }

  async updateMilestone(id: number, milestone: Partial<Milestone>): Promise<Milestone> {
    return this.put<Milestone>(`/milestones/${id}`, milestone);
  }

  async deleteMilestone(id: number): Promise<void> {
    return this.delete<void>(`/milestones/${id}`);
  }

  /**
   * Get project portfolio with detailed metrics for all projects
   */
  async getProjectPortfolio(): Promise<ProjectPortfolio> {
    try {
      return await this.get<ProjectPortfolio>('/executive/project-portfolio');
    } catch (error) {
      console.error('[api] Unable to load reports', error);
      throw error;
    }
  }

  // Alerts
  async getAlertConfig(): Promise<AlertConfig> {
    return this.get<AlertConfig>('/alerts/config');
  }

  async updateAlertConfig(config: Partial<AlertConfig>): Promise<AlertConfig> {
    return this.put<AlertConfig>('/alerts/config', config);
  }

  async getAlertLogs(filters?: { limit?: number; offset?: number; alert_type?: string; project_id?: string }): Promise<AlertLog[]> {
    const params: Record<string, string> = {};
    if (filters?.limit) params.limit = String(filters.limit);
    if (filters?.offset) params.offset = String(filters.offset);
    if (filters?.alert_type) params.alert_type = filters.alert_type;
    if (filters?.project_id) params.project_id = filters.project_id;
    return this.get<AlertLog[]>('/alerts/logs', params);
  }

  async testAlerts(): Promise<AlertTestResult> {
    return this.post<AlertTestResult>('/alerts/test');
  }

  async getUserNotifications(): Promise<{ unread_count: number; notifications: AlertLog[] }> {
    return this.get<{ unread_count: number; notifications: AlertLog[] }>('/alerts/notifications');
  }

  async markNotificationAsRead(notificationId: number): Promise<{ message: string }> {
    return this.put<{ message: string }>(`/alerts/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead(): Promise<{ message: string }> {
    return this.put<{ message: string }>('/alerts/notifications/mark-all-read');
  }

  // Settings
  async getSettings(category?: string, includeSystem: boolean = true): Promise<GlobalSetting[]> {
    const params: Record<string, string> = {};
    if (category) params.category = category;
    params.include_system = String(includeSystem);
    return this.get<GlobalSetting[]>('/settings', params);
  }

  async getSetting(key: string): Promise<GlobalSetting> {
    // Append tenant_id when selected (tenant-scoped overrides)
    let url = `/settings/${key}`;
    try {
      if (typeof window !== 'undefined') {
        const selectedTenantId = window.localStorage?.getItem('selectedTenantId');
        if (selectedTenantId && selectedTenantId !== 'null') {
          const tid = parseInt(selectedTenantId, 10);
          if (!Number.isNaN(tid) && tid > 0) {
            url = `${url}?tenant_id=${encodeURIComponent(String(tid))}`;
          }
        }
      }
    } catch {}
    return this.get<GlobalSetting>(url);
  }

  async updateSetting(key: string, value: string): Promise<GlobalSetting> {
    // Send tenant_id when a tenant is selected to persist override in that tenant only
    let url = `/settings/${key}`;
    try {
      if (typeof window !== 'undefined') {
        const selectedTenantId = window.localStorage?.getItem('selectedTenantId');
        if (selectedTenantId && selectedTenantId !== 'null') {
          const tid = parseInt(selectedTenantId, 10);
          if (!Number.isNaN(tid) && tid > 0) {
            url = `${url}?tenant_id=${encodeURIComponent(String(tid))}`;
          }
        }
      }
    } catch {}
    return this.put<GlobalSetting>(url, { value });
  }

  async createSetting(setting: { key: string; value: string; description?: string; category?: string }): Promise<GlobalSetting> {
    // Create tenant-scoped override when a tenant is selected
    let url = '/settings';
    try {
      if (typeof window !== 'undefined') {
        const selectedTenantId = window.localStorage?.getItem('selectedTenantId');
        if (selectedTenantId && selectedTenantId !== 'null') {
          const tid = parseInt(selectedTenantId, 10);
          if (!Number.isNaN(tid) && tid > 0) {
            url = `${url}?tenant_id=${encodeURIComponent(String(tid))}`;
          }
        }
      }
    } catch {}
    return this.post<GlobalSetting>(url, setting);
  }

  async deleteSetting(key: string): Promise<{ message: string }> {
    // Delete tenant override (if exists) when a tenant is selected; otherwise delete global
    let url = `/settings/${key}`;
    try {
      if (typeof window !== 'undefined') {
        const selectedTenantId = window.localStorage?.getItem('selectedTenantId');
        if (selectedTenantId && selectedTenantId !== 'null') {
          const tid = parseInt(selectedTenantId, 10);
          if (!Number.isNaN(tid) && tid > 0) {
            url = `${url}?tenant_id=${encodeURIComponent(String(tid))}`;
          }
        }
      }
    } catch {}
    return this.delete<{ message: string }>(url);
  }

  // Committee Approvals
  async getCommitteeApprovals(committeeId?: string): Promise<{ items: CommitteeApprovalRequest[]; total: number }> {
    const params: Record<string, string> = {};
    if (committeeId) params.committee_id = committeeId;
    return this.get<{ items: CommitteeApprovalRequest[]; total: number }>('/committee/approvals', params);
  }

  async getApprovalsForIntake(intakeId: number): Promise<{ items: CommitteeApprovalRequest[]; total: number }> {
    return this.get<{ items: CommitteeApprovalRequest[]; total: number }>(`/committee/approvals/intake/${intakeId}`);
  }

  async createCommitteeApprovalRequest(intakeId: number, committeeId: string, notes?: string): Promise<CommitteeApprovalRequest> {
    return this.post<CommitteeApprovalRequest>('/committee/approvals', {
      intake_request_id: intakeId,
      committee_id: committeeId,
      notes,
    });
  }

  async approveCommitteeRequest(approvalId: string, data: { notes?: string }): Promise<CommitteeApprovalRequest> {
    return this.post<CommitteeApprovalRequest>(`/committee/approvals/${approvalId}/approve`, data);
  }

  async rejectCommitteeRequest(approvalId: string, data: { rejection_reason: string; notes?: string }): Promise<CommitteeApprovalRequest> {
    return this.post<CommitteeApprovalRequest>(`/committee/approvals/${approvalId}/reject`, data);
  }

  // Unified approval pattern (new - T047)
  async approveCommitteeRequestUnified(
    approvalId: string,
    data: { is_approved: boolean; rejection_reason?: string; notes?: string }
  ): Promise<CommitteeApprovalRequest> {
    return this.post<CommitteeApprovalRequest>(`/committee/approvals/${approvalId}/approve`, data);
  }

  // Committees
  async listCommittees(filters?: {
    search?: string;
    is_active?: boolean | 'all';
    has_sovereign?: boolean | 'all';
    tenant_id?: number | null;
  }): Promise<{ items: Committee[] }> {
    // Build params for both possible backends
    const params: Record<string, string> = {};
    if (filters?.search) params.search = filters.search;
    if (filters?.is_active !== undefined && filters?.is_active !== 'all') {
      // New approval-committee API uses active_only; legacy uses is_active
      params.is_active = String(filters.is_active);
      (params as any).active_only = String(filters.is_active);
    }
    if (filters?.has_sovereign !== undefined && filters?.has_sovereign !== 'all') {
      params.has_sovereign = String(filters.has_sovereign);
    }
    // Pass tenant_id to API via params if specified (backend will use it to filter)
    if (filters?.tenant_id !== undefined && filters?.tenant_id !== null) {
      params.tenant_id = String(filters.tenant_id);
    }

    // Normalizer to ensure consistent Committee shape (id as string)
    const normalizeCommittees = (items: any[]): Committee[] => {
      return (items || []).map((raw) => ({
        id: String(raw?.id ?? ''),
        name: raw?.name ?? '',
        description: raw?.description ?? undefined,
        is_active: Boolean(raw?.is_active ?? true),
        quorum_min: Number(raw?.quorum_min ?? raw?.quorumMin ?? 0),
        votes_required: Number(raw?.votes_required ?? raw?.votesRequired ?? 0),
        scope_type: raw?.scope_type ?? raw?.scopeType ?? undefined,
        scope_ref_id: raw?.scope_ref_id ?? raw?.scopeRefId ?? undefined,
        created_by: raw?.created_by ? String(raw.created_by) : undefined,
        created_at: raw?.created_at ?? raw?.createdAt ?? '',
        updated_at: raw?.updated_at ?? raw?.updatedAt ?? '',
        members: Array.isArray(raw?.members) ? raw.members : undefined,
        member_count: typeof raw?.member_count === 'number' ? raw.member_count : (Array.isArray(raw?.members) ? raw.members.length : undefined),
        sovereign_count: typeof raw?.sovereign_count === 'number' ? raw.sovereign_count : undefined,
      }));
    };

    try {
      // PREFERRED: approval-committees API has better multi-tenant support (FR-021b)
      // Only falls back to legacy /committees if approval-committees returns 404/403/405
      const resp = await this.get<any>('/approval-committees', params).catch((e) => { throw e; });
      const items: any[] = Array.isArray(resp?.items) ? resp.items : (Array.isArray(resp) ? resp : []);
      return { items: normalizeCommittees(items) };
    } catch (err: any) {
      const status = err?.status || err?.response?.status || 0;
      // Fallback: try legacy committees API only if approval-committees not available
      if (status === 404 || status === 403 || status === 405) {
        try {
          const legacy = await this.get<any[]>('/committees', params);
          const items: any[] = Array.isArray(legacy) ? legacy : (Array.isArray((legacy as any)?.items) ? (legacy as any).items : []);
          return { items: normalizeCommittees(items) };
        } catch (legacyErr) {
          throw err; // Re-throw original error if legacy also fails
        }
      }
      throw err;
    }
  }

  async getCommittee(id: string): Promise<Committee> {
    const isUuid = this.isUuid(id);

    // Prefer the endpoint that matches the identifier format to avoid noisy 4xx logs
    if (isUuid) {
      try {
        const legacy = await this.get<any>(`/committees/${encodeURIComponent(id)}`);
        return this.normalizeCommittee(legacy);
      } catch (err: any) {
        const status = err?.status || err?.response?.status || 0;
        // Fallback to approval-committees (some envs may use numeric strings for UUIDs)
        if (status === 404 || status === 403 || status === 405) {
          const raw = await this.get<any>(`/approval-committees/${encodeURIComponent(id)}`).catch((e) => { throw e; });
          return this.normalizeCommittee(raw);
        }
        throw err;
      }
    } else {
      try {
        const raw = await this.get<any>(`/approval-committees/${encodeURIComponent(id)}`);
        return this.normalizeCommittee(raw);
      } catch (err: any) {
        const status = err?.status || err?.response?.status || 0;
        // Fallback for backends where approval-committees expects int but we have legacy UUIDs, or any 4xx we can recover from
        if (status === 404 || status === 403 || status === 405 || status === 422 || status === 400) {
          const legacy = await this.get<any>(`/committees/${encodeURIComponent(id)}`).catch((e) => { throw e; });
          return this.normalizeCommittee(legacy);
        }
        throw err;
      }
    }
  }

  async createCommittee(data: any): Promise<Committee> {
    // If tenant_id is provided (numeric), use approval-committees API
    const tenantIdRaw = (data as any)?.tenant_id;
    const tenantId = typeof tenantIdRaw === 'number' ? tenantIdRaw : (tenantIdRaw ? Number(tenantIdRaw) : null);

    if (tenantId && Number.isFinite(tenantId)) {
      const membersArr: any[] = Array.isArray((data as any)?.members) ? (data as any).members : [];
      const member_ids: number[] = membersArr
        .map((m) => (typeof m?.user_id === 'number' ? m.user_id : Number(m?.user_id)))
        .filter((id) => Number.isFinite(id));

      const majorityHolder = membersArr.find((m) => Boolean(m?.is_sovereign));
      const majority_holder_id = majorityHolder
        ? (typeof majorityHolder.user_id === 'number' ? majorityHolder.user_id : Number(majorityHolder.user_id))
        : undefined;

      const payload = {
        name: (data as any)?.name,
        description: (data as any)?.description,
        is_active: (data as any)?.is_active ?? true,
        tenant_id: tenantId,
        member_ids,
        majority_holder_id,
      };

      const created = await this.post<any>('/approval-committees', payload);
      return this.normalizeCommittee(created);
    }

    // Legacy committees API (UUID-based)
    return this.post<Committee>('/committees', data);
  }

  async updateCommittee(id: string, data: any): Promise<Committee> {
    const isUuid = this.isUuid(id);
    if (isUuid) {
      return this.put<Committee>(`/committees/${encodeURIComponent(id)}`, data);
    }
    // Numeric IDs belong to approval-committees API
    return this.put<Committee>(`/approval-committees/${encodeURIComponent(id)}`, data);
  }

  async deleteCommittee(id: string): Promise<void> {
    const isUuid = this.isUuid(id);
    if (isUuid) {
      await this.delete<void>(`/committees/${encodeURIComponent(id)}`);
      return;
    }
    // Numeric IDs belong to approval-committees API
    await this.delete<void>(`/approval-committees/${encodeURIComponent(id)}`);
  }

  // Financial reports (portfolio + project drill-down)
  async getFinancialSummary(params?: {
    include_archived?: boolean;
    budget_min?: number;
    budget_max?: number;
    category_id?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<PortfolioFinancialSummaryResponse> {
    const query: Record<string, string> = {};

    if (params?.include_archived !== undefined) query.include_archived = String(params.include_archived);
    if (params?.budget_min !== undefined) query.budget_min = String(params.budget_min);
    if (params?.budget_max !== undefined) query.budget_max = String(params.budget_max);
    if (params?.category_id !== undefined) query.category_id = String(params.category_id);
    if (params?.start_date) query.start_date = params.start_date;
    if (params?.end_date) query.end_date = params.end_date;

    return this.get<PortfolioFinancialSummaryResponse>('/reports/financial', query);
  }

  async getProjectFinancialDetail(projectId: number): Promise<ProjectFinancialDetailResponse> {
    return this.get<ProjectFinancialDetailResponse>(`/reports/financial/projects/${projectId}`);
  }

  // Audit Logs
  /**
   * Fetch audit logs with pagination and filtering
   * 
   * @param params Query parameters (page, page_size, filters)
   * @returns Promise with audit logs list response
   */
  async fetchAuditLogs(params: {
    page?: number;
    page_size?: number;
    user_id?: number | null;
    entity?: string | null;
    entity_id?: number | null;
    field_name?: string | null;
    start_date?: string | null;
    end_date?: string | null;
  }): Promise<any> {
    const queryParams: Record<string, string> = {};

    // Add pagination parameters
    if (params.page) queryParams.page = String(params.page);
    if (params.page_size) queryParams.page_size = String(params.page_size);

    // Add filter parameters if provided
    if (params.user_id) queryParams.user_id = String(params.user_id);
    if (params.entity) queryParams.entity = params.entity;
    if (params.entity_id) queryParams.entity_id = String(params.entity_id);
    if (params.field_name) queryParams.field_name = params.field_name;
    if (params.start_date) queryParams.start_date = params.start_date;
    if (params.end_date) queryParams.end_date = params.end_date;

    return this.get<any>('/audit', queryParams);
  }

  /**
   * Get recent updates feed (audit-based)
   * @param filters Optional filters for project_id, item_type, days, limit
   * @returns Recent audit entries with summary and target URLs
   */
  async getRecentUpdates(filters?: AuditFeedFilters): Promise<AuditFeedResponse> {
    const params: Record<string, string> = {};

    if (filters?.project_id) params.project_id = String(filters.project_id);
    if (filters?.item_type) params.item_type = filters.item_type;
    if (filters?.days) params.days = String(filters.days);
    if (filters?.limit) params.limit = String(filters.limit);

    return this.get<AuditFeedResponse>('/updates/recent', params);
  }

  /**
   * T020: Get audit feature flag state
   * @returns Object with { key: "audit.enabled", enabled: boolean }
   */
  async getAuditEnabled(tenantId?: number | null): Promise<{ key: string; enabled: boolean; value_json?: any }> {
    let url = '/settings/audit.enabled';
    const effectiveTenantId = tenantId ?? (() => {
      try {
        if (typeof window !== 'undefined') {
          const stored = window.localStorage?.getItem('selectedTenantId');
          if (stored && stored !== 'null') {
            const tid = parseInt(stored, 10);
            if (!Number.isNaN(tid) && tid > 0) return tid;
          }
        }
      } catch {}
      return undefined;
    })();

    if (effectiveTenantId !== undefined && effectiveTenantId !== null) {
      url = `${url}?tenant_id=${encodeURIComponent(String(effectiveTenantId))}`;
    }

    return this.get<{ key: string; enabled: boolean; value_json?: any }>(url);
  }

  /**
   * T021: Update audit feature flag state (admin-only)
   * @param enabled Boolean indicating whether audit should be enabled
   * @returns Object with updated state
   */
  async updateAuditEnabled(enabled: boolean, tenantId?: number | null): Promise<{ key: string; enabled: boolean; message: string }> {
    let url = `/settings/audit.enabled?enabled=${enabled}`;
    const effectiveTenantId = tenantId ?? (() => {
      try {
        if (typeof window !== 'undefined') {
          const stored = window.localStorage?.getItem('selectedTenantId');
          if (stored && stored !== 'null') {
            const tid = parseInt(stored, 10);
            if (!Number.isNaN(tid) && tid > 0) return tid;
          }
        }
      } catch {}
      return undefined;
    })();

    if (effectiveTenantId !== undefined && effectiveTenantId !== null) {
      url = `${url}&tenant_id=${encodeURIComponent(String(effectiveTenantId))}`;
    }

    return this.client.put<any, AxiosResponse<{ key: string; enabled: boolean; message: string }>>(
      url
    ).then(res => res.data);
  }
}

export const api = new ApiService();
export default ApiService;



